import React, { useState, useEffect } from 'react';
import { Card, Radio, Typography, Steps, Modal, Button, Tooltip } from 'antd';
import {
	UnorderedListOutlined,
	GlobalOutlined,
	BellOutlined,
	EnvironmentOutlined,
	CheckOutlined,
	SendOutlined,
	WarningOutlined,
	EyeOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment';
import { getSignedUrl } from 'store/commonSlice';
import RCTable from 'components/custom-table/RCTable';
import { TripNodesTableConfig, LocationIconWrapper } from 'utils/table-config/TripNodesTableConfig';
import { NODE_STATUS, normalizeNodeStatus } from 'utils/tripNodeStatus';
import { TRIP_STATUS_ID } from 'utils/tripStatus';
import MapView from './MapView';

const { Title, Text } = Typography;
const { Step } = Steps;

// Style constants
const BORDER_STYLE = '1px solid #f0f0f0';

const TableWrapper = styled.div`
	.ant-table-thead > tr > th {
		background-color: #1f3e5a !important;
		color: #ffffff;
		border-bottom: none;
	}
	.ant-table-tbody > tr > td {
		border-bottom: 1px solid #f0f0f0;
		cursor: pointer;
	}
	.ant-pagination {
		display: none !important;
	}
`;

const CustomSteps = styled(Steps)`
	.ant-steps-item {
		min-width: 140px;
	}
	.ant-steps-item-icon {
		background: transparent !important;
		border: none !important;
	}
	.ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-tail::after {
		background-color: #52c41a !important;
	}
	.ant-steps-item-title {
		text-align: center;
	}
	.ant-steps-item-description {
		text-align: center;
	}
`;

const TripCard = styled(Card)`
	border-radius: 8px;
	margin-bottom: 16px;
	border: ${BORDER_STYLE};
`;

const CardHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 24px;
	border-bottom: ${BORDER_STYLE};
`;

const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
`;

const CardTitle = styled(Title)`
	&& {
		margin: 0;
		margin-right: 12px;
	}
`;

const HeaderIconContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border-radius: 6px;
	border: ${BORDER_STYLE};
	background-color: #fafafa;
`;

const BellIcon = styled(BellOutlined)`
	color: #eae3e3;
	font-size: 18px;
`;

const CardBody = styled.div`
	padding: 24px;
`;

const StepsContainer = styled.div`
	margin-bottom: 32px;
	padding: 0 24px 16px;
	overflow-x: auto;
	overflow-y: hidden;

	/* Custom Scrollbar */
	&::-webkit-scrollbar {
		height: 6px;
	}
	&::-webkit-scrollbar-track {
		background: #f1f1f1;
		border-radius: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c1c1c1;
		border-radius: 4px;
	}
	&::-webkit-scrollbar-thumb:hover {
		background: #a8a8a8;
	}
`;

const CompletedStepIcon = styled.div`
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background-color: #52c41a;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	margin: 0 auto;
`;

const SlaBreachWrapper = styled.div`
	position: relative;
	margin: 0 auto;
	width: 32px;
	height: 32px;
`;

const SlaBreachStepIcon = styled.div`
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background-color: #faad14;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
`;

const SlaWarningBadge = styled.div`
	position: absolute;
	top: -4px;
	right: -4px;
	background-color: red;
	border-radius: 50%;
	width: 16px;
	height: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #ff4d4f;
`;

const WarningIcon = styled(WarningOutlined)`
	color: #f1eeeeff;
	font-size: 12px;
`;

const InProgressStepIcon = styled.div`
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background-color: #1890ff;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	margin: 0 auto;
`;

const PendingStepIcon = styled.div`
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background-color: #f5f5f5;
	border: 1px solid #d9d9d9;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #8c8c8c;
	margin: 0 auto;
	font-size: 12px;
`;

const StepTitle = styled.span`
	font-size: 12px;
	font-weight: bold;
	color: #262626;
	margin: 8px auto 0;
	/*
	 * Four lines, then an ellipsis. A node_name is often a full postal address, and
	 * unclamped it wrapped to a dozen-plus lines — pushing the node table, which carries
	 * that same address in full, off the bottom of the screen. Four lines is enough to
	 * reach the locality, which is what actually identifies the stop; the table is where
	 * you read the rest.
	 */
	display: -webkit-box;
	-webkit-line-clamp: 4;
	-webkit-box-orient: vertical;
	overflow: hidden;
	max-width: 240px;
	/* A single unbroken token (a long code, a URL) would otherwise widen the column. */
	word-break: break-word;
`;

const MapPlaceholder = styled.div`
	height: 400px;
	background-color: #f0f2f5;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
`;

const ModalDetailCard = styled.div`
	background-color: #f9fafb;
	border-radius: 12px;
	padding: 16px 20px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 24px;
`;

const ModalTimeContainer = styled.div`
	flex: 0 0 120px;
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	margin: 0 12px;
`;

function TripProgressSection({ tripId, trackingInfo, liveTracking, tripInfo }) {
	const dispatch = useDispatch();
	const [viewType, setViewType] = useState('progress');
	const [selectedNode, setSelectedNode] = useState(null);
	const [imageModalVisible, setImageModalVisible] = useState(false);
	const [modalImage, setModalImage] = useState('');

	// Once a trip is Closed its progress figure is stale/meaningless (spec §7) — a live
	// percent bar has no honest value to show, so a static "Completed" badge replaces it.
	// The same closure flag also fences node status display below (spec §7 safety net):
	// the backend force-completes nodes at closure, but nothing stops a stale "Pending"
	// from rendering here if that write raced the read, so it is normalized defensively.
	const isTripClosed = Number(tripInfo?.trip_status_id) === TRIP_STATUS_ID.CLOSED;

	const tableNodes = trackingInfo || [];

	const nodes = liveTracking?.nodes?.length ? liveTracking.nodes : tableNodes;
	const [rowData, setRowData] = useState({ rows: [], count: 0 });
	const [paginateConfig, setpaginateConfig] = useState({
		limit: 10,
		currentPage: 1,
		offset: 0,
	});

	useEffect(() => {
		setRowData({ rows: tableNodes, count: tableNodes.length });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trackingInfo]);

	const currentStepIdx = () => {
		const inProgressIdx = nodes.findIndex(
			(n) => normalizeNodeStatus(n.status, isTripClosed) === NODE_STATUS.IN_PROGRESS
		);
		if (inProgressIdx !== -1) return inProgressIdx;

		const completedIdxs = nodes.reduce((acc, n, i) => {
			if (normalizeNodeStatus(n.status, isTripClosed) === NODE_STATUS.COMPLETED) acc.push(i);
			return acc;
		}, []);
		if (completedIdxs.length) return completedIdxs[completedIdxs.length - 1];

		return 0;
	};

	const handleRowClick = (record) => {
		const details = {
			eventName: 'PoD Collected',
			statusText: record.pod_image_url || record.odometer_image_url ? 'Completed' : 'Pending',
			time: record.last_updated
				? moment(record.last_updated).add(5, 'hours').add(30, 'minutes').format('hh:mm A')
				: '-',
			hasDocument: !!record.pod_image_url,
			podUrl: record.pod_image_url,
			hasOdometer: !!record.odometer_image_url,
			odometerUrl: record.odometer_image_url,
		};

		setSelectedNode({ ...record, details });
	};
	const openPodImage = async (fileUrl, type) => {
		if (!fileUrl) return;

		let entityName = '';

		if (type === 'pod') entityName = 'pod_image';
		if (type === 'odometer') entityName = 'trip';

		const fileKey = fileUrl.split('/').pop().split('?')[0];

		const data = {
			url_type: 'view',
			data: {
				entity: entityName,
				keys: [fileKey],
			},
		};

		const res = await dispatch(getSignedUrl(data));
		const signedUrl = res?.data?.[0]?.url;

		if (!signedUrl) return;

		setImageModalVisible(true);
		setModalImage(signedUrl);
	};
	const columns = TripNodesTableConfig(normalizeNodeStatus, isTripClosed);

	return (
		<TripCard bodyStyle={{ padding: '0px' }}>
			<CardHeader>
				<HeaderLeft>
					<CardTitle level={5}>Trip Progress</CardTitle>
					<HeaderIconContainer>
						<BellIcon />
					</HeaderIconContainer>
				</HeaderLeft>

				<Radio.Group
					value={viewType}
					onChange={(e) => setViewType(e.target.value)}
					optionType="button"
					buttonStyle="solid"
				>
					<Radio.Button value="progress">
						<UnorderedListOutlined style={{ marginRight: '6px' }} />
						Progress
					</Radio.Button>
					<Radio.Button value="map">
						<GlobalOutlined style={{ marginRight: '6px' }} />
						Map View
					</Radio.Button>
				</Radio.Group>
			</CardHeader>

			<CardBody>
				{viewType === 'progress' ? (
					<>
						<StepsContainer>
							<CustomSteps current={currentStepIdx()} labelPlacement="vertical">
								{nodes.map((node, index) => {
									let stepIcon;
									const nodeStatus = normalizeNodeStatus(
										node.status,
										isTripClosed
									);

									if (nodeStatus === NODE_STATUS.COMPLETED) {
										stepIcon = (
											<CompletedStepIcon>
												<CheckOutlined style={{ fontSize: '16px' }} />
											</CompletedStepIcon>
										);
									} else if (nodeStatus === NODE_STATUS.DELAYED) {
										stepIcon = (
											<SlaBreachWrapper>
												<SlaBreachStepIcon>
													<CheckOutlined style={{ fontSize: '16px' }} />
												</SlaBreachStepIcon>
												<SlaWarningBadge>
													<WarningIcon />
												</SlaWarningBadge>
											</SlaBreachWrapper>
										);
									} else if (nodeStatus === NODE_STATUS.IN_PROGRESS) {
										stepIcon = (
											<InProgressStepIcon>
												<SendOutlined style={{ fontSize: '16px' }} />
											</InProgressStepIcon>
										);
									} else {
										stepIcon = <PendingStepIcon>{index + 1}</PendingStepIcon>;
									}

									return (
										<Step
											key={node.node_id || index}
											icon={stepIcon}
											title={
												<Tooltip title={node.node_name}>
													<StepTitle>
														{node.node_name
															?.replace?.(/[_-]/g, ' ')
															?.toUpperCase?.()}
													</StepTitle>
												</Tooltip>
											}
										/>
									);
								})}
							</CustomSteps>
						</StepsContainer>

						<TableWrapper>
							<RCTable
								columns={columns}
								tableDatas={[rowData, setRowData]}
								togglePaginate={[paginateConfig, setpaginateConfig]}
								hasPagination={false}
								pagination={false}
								scroll={{ y: 300 }}
								rowKey="id"
								onRow={(record) => ({
									onClick: () => handleRowClick(record),
								})}
							/>
						</TableWrapper>
					</>
				) : (
					<MapPlaceholder>
						<MapView
							tripId={tripId}
							trackingInfo={trackingInfo}
							liveTracking={liveTracking}
							tripInfo={tripInfo}
						/>
					</MapPlaceholder>
				)}
			</CardBody>
			<Modal
				open={!!selectedNode}
				onCancel={() => setSelectedNode(null)}
				footer={
					<div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
						<Button
							type="primary"
							style={{
								backgroundColor: '#1f3e5a',
								width: '140px',
								borderRadius: '6px',
							}}
							onClick={() => setSelectedNode(null)}
						>
							Close
						</Button>
					</div>
				}
				closeIcon={<span>✕</span>}
				bodyStyle={{ padding: '32px 24px' }}
				centered
			>
				{selectedNode && (
					<div>
						<div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
							<LocationIconWrapper
								style={{
									background: '#e6f7ff',
									width: '48px',
									height: '48px',
									borderRadius: '12px',
								}}
							>
								<EnvironmentOutlined
									style={{ color: '#1890ff', fontSize: '20px' }}
								/>
							</LocationIconWrapper>

							<div>
								<Title level={4} style={{ margin: 0, color: '#1f3e5a' }}>
									{selectedNode.node_name}
								</Title>
								<Text type="secondary">{selectedNode.location_address || '-'}</Text>
							</div>
						</div>{' '}
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '16px',
								marginTop: '16px',
							}}
						>
							{tripInfo?.pod_required && (
								<ModalDetailCard style={{ marginTop: 0 }}>
									<div style={{ display: 'flex', alignItems: 'center' }}>
										<span
											style={{
												display: 'inline-block',
												width: '9px',
												height: '9px',
												borderRadius: '50%',
												backgroundColor: selectedNode.pod_image_url
													? '#52c41a'
													: '#faad14',
												marginRight: '8px',
												flexShrink: 0,
											}}
										/>
										<div>
											<Text strong style={{ fontSize: '15px' }}>
												PoD Collected
											</Text>
											<br />
											<Text
												style={{
													color: selectedNode.pod_image_url
														? '#52c41a'
														: '#faad14',
													fontSize: '13px',
												}}
											>
												{selectedNode.pod_image_url
													? 'Completed'
													: 'Pending'}
											</Text>
										</div>
									</div>
									<ModalTimeContainer>
										<Text strong style={{ color: '#595959' }}>
											{selectedNode.last_updated
												? moment(selectedNode.last_updated)
														.add(5, 'hours')
														.add(30, 'minutes')
														.format('hh:mm A')
												: '-'}
										</Text>
									</ModalTimeContainer>
									{selectedNode.pod_image_url && (
										<div
											style={{
												background: '#fff',
												padding: '6px 10px',
												borderRadius: '6px',
												boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
												cursor: 'pointer',
											}}
										>
											<EyeOutlined
												style={{ color: '#1890ff', fontSize: '16px' }}
												onClick={() =>
													openPodImage(selectedNode.pod_image_url, 'pod')
												}
											/>
										</div>
									)}
								</ModalDetailCard>
							)}

							{selectedNode.odometer_image_url && (
								<ModalDetailCard
									style={{
										marginTop: 0,
										flexDirection: 'column',
										alignItems: 'stretch',
										gap: '12px',
									}}
								>
									<div
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
										}}
									>
										<div style={{ display: 'flex', alignItems: 'center' }}>
											<span
												style={{
													display: 'inline-block',
													width: '9px',
													height: '9px',
													borderRadius: '50%',
													backgroundColor: '#52c41a',
													marginRight: '8px',
													flexShrink: 0,
												}}
											/>
											<div>
												<Text strong style={{ fontSize: '15px' }}>
													Odometer Collected
												</Text>
												<br />
												<Text
													style={{ color: '#52c41a', fontSize: '13px' }}
												>
													Completed
												</Text>
											</div>
										</div>
										<ModalTimeContainer>
											<Text strong style={{ color: '#595959' }}>
												{selectedNode.last_updated
													? moment(selectedNode.last_updated)
															.add(5, 'hours')
															.add(30, 'minutes')
															.format('hh:mm A')
													: '-'}
											</Text>
										</ModalTimeContainer>
										<div
											style={{
												background: '#fff',
												padding: '6px 10px',
												borderRadius: '6px',
												boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
												cursor: 'pointer',
											}}
										>
											<EyeOutlined
												style={{ color: '#1890ff', fontSize: '16px' }}
												onClick={() =>
													openPodImage(
														selectedNode.odometer_image_url,
														'odometer'
													)
												}
											/>
										</div>
									</div>

									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '10px',
										}}
									>
										<div
											style={{
												width: '28px',
												height: '28px',
												borderRadius: '50%',
												border: '1.5px solid #1890ff',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												flexShrink: 0,
											}}
										>
											<svg
												viewBox="0 0 24 24"
												width="14"
												height="14"
												fill="none"
												stroke="#1890ff"
												strokeWidth="2"
											>
												<circle cx="12" cy="12" r="10" />
												<polyline points="12 6 12 12 16 14" />
											</svg>
										</div>
										<div>
											<Text
												style={{
													fontSize: '11px',
													color: '#8c8c8c',
													display: 'block',
												}}
											>
												Time Stamp
											</Text>
											<Text
												strong
												style={{ fontSize: '13px', color: '#262626' }}
											>
												{selectedNode.last_updated
													? moment(selectedNode.last_updated)
															.add(5, 'hours')
															.add(30, 'minutes')
															.format('MM/DD/YYYY, hh:mm:ss A')
													: '-'}
											</Text>
										</div>
									</div>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '10px',
										}}
									>
										<div
											style={{
												width: '28px',
												height: '28px',
												borderRadius: '50%',
												border: '1.5px solid #1890ff',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												flexShrink: 0,
											}}
										>
											<svg
												viewBox="0 0 24 24"
												width="14"
												height="14"
												fill="none"
												stroke="#1890ff"
												strokeWidth="2"
											>
												<circle cx="12" cy="12" r="3" />
												<line x1="12" y1="2" x2="12" y2="6" />
												<line x1="12" y1="18" x2="12" y2="22" />
												<line x1="2" y1="12" x2="6" y2="12" />
												<line x1="18" y1="12" x2="22" y2="12" />
											</svg>
										</div>
										<div>
											<Text
												style={{
													fontSize: '11px',
													color: '#8c8c8c',
													display: 'block',
												}}
											>
												Coordinates ( Lat, Long )
											</Text>
											<Text
												strong
												style={{ fontSize: '13px', color: '#262626' }}
											>
												{selectedNode.latitude != null &&
												selectedNode.longitude != null
													? `${Number(selectedNode.latitude).toFixed(
															4
													  )}, ${Number(selectedNode.longitude).toFixed(
															4
													  )}`
													: '-'}
											</Text>
										</div>
									</div>
								</ModalDetailCard>
							)}
						</div>
					</div>
				)}
			</Modal>
			<Modal
				open={imageModalVisible}
				footer={null}
				onCancel={() => setImageModalVisible(false)}
				centered
				width={modalImage?.split('?')[0]?.toLowerCase()?.endsWith('.pdf') ? 800 : 520}
			>
				{modalImage?.split('?')[0]?.toLowerCase()?.endsWith('.pdf') ? (
					<iframe
						src={modalImage}
						title="Document Viewer"
						width="100%"
						height="600px"
						style={{ border: 'none', borderRadius: '8px' }}
					/>
				) : (
					<img
						key={modalImage}
						src={modalImage}
						alt="POD"
						style={{ width: '100%', borderRadius: '8px' }}
					/>
				)}
			</Modal>
		</TripCard>
	);
}

TripProgressSection.propTypes = {
	tripId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	trackingInfo: PropTypes.array,
	liveTracking: PropTypes.object,
	tripInfo: PropTypes.object,
};
export default TripProgressSection;
